import React, { useEffect, useRef, useState } from 'react';

/**
 * MapDisplay Component
 * Displays a Google Map with a marker at the specified location
 * 
 * Props:
 * - location: { lat: number, lng: number } | null
 * - apiKey: string (Google Maps API key)
 * - title: string (optional - marker title)
 * - height: string (optional - default '300px')
 */
export default function MapDisplay({ location, apiKey, title, height = '300px' }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Load Google Maps script
  useEffect(() => {
    if (!apiKey) {
      setError('Google Maps API key is required');
      return;
    }

    // Check if already loaded
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }

    // Load script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, [apiKey]);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !location || !location.lat || !location.lng) return;

    // Create map if it doesn't exist
    if (!mapInstanceRef.current) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: location,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });

      mapInstanceRef.current = map;
    }

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Create new marker
    const marker = new window.google.maps.Marker({
      position: location,
      map: mapInstanceRef.current,
      title: title || 'Location',
      animation: window.google.maps.Animation.DROP,
    });

    markerRef.current = marker;

    // Center map on location
    mapInstanceRef.current.setCenter(location);
  }, [isLoaded, location, title]);

  if (!location || !location.lat || !location.lng) {
    return null; // Don't render anything if no location
  }

  if (error) {
    return (
      <div style={{ padding: '20px', background: '#fee', border: '1px solid #fcc', color: '#c33', height }}>
        <strong>Error:</strong> {error}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ padding: '20px', background: '#f5f5f5', border: '1px solid #ddd', textAlign: 'center', height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Loading map...
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      style={{
        width: '100%',
        height,
        border: '1px solid #ddd',
      }}
    />
  );
}
